import { createTheme } from '@mui/material/styles';

// New design language: Manrope everywhere (headings at 800 weight)
const fontFamilyManrope = "'Manrope', system-ui, -apple-system, 'Segoe UI', Arial, sans-serif";
const fontWeightRegular = 400;
const fontWeightMedium = 500;
const fontWeightSemibold = 600;
const fontWeightBold = 700;
const colorPrimary = '#12211D';
const colorSecondary = '#5B6B66';

declare module '@mui/material/styles' {
  interface TypographyVariants {
    body3: React.CSSProperties;
    body4: React.CSSProperties;
    body5: React.CSSProperties;
    mainButton: React.CSSProperties;
    ctaButton: React.CSSProperties;
  }

  interface TypographyVariantsOptions {
    body3?: React.CSSProperties;
    body4?: React.CSSProperties;
    body5?: React.CSSProperties;
    mainButton?: React.CSSProperties;
    ctaButton?: React.CSSProperties;
  }

  interface Palette {
    customColor?: Palette['primary'];
  }
  interface PaletteOptions {
    customColor?: PaletteOptions['primary'];
  }
}

declare module '@mui/material/Typography' {
  interface TypographyPropsVariantOverrides {
    body3: true;
    body4: true;
    body5: true;
    mainButton: true;
    ctaButton: true;
  }
}

export const printtTheme = createTheme({
  spacing: 8,
  breakpoints: {
    values: {
      xs: 320,
      sm: 750,
      md: 970,
      lg: 1170,
      xl: 2000,
    },
  },
  components: {
    MuiContainer: {
      styleOverrides: {
        root: {
          overflow: 'visible', // Allow scaled content to be visible
          position: 'relative', // Ensure proper stacking context
          zIndex: 1,
        },
        maxWidthXl: {
          overflow: 'visible',
        },
      },
    },
    MuiCssBaseline: {
      styleOverrides: {
        body: {
          margin: 0,
          padding: 0,
          fontFamily: fontFamilyManrope,
          fontWeight: fontWeightRegular,
          color: colorPrimary,
          backgroundColor: '#F7F9F8',
          WebkitFontSmoothing: 'antialiased',
        },
        '::selection': {
          background: '#BFE5DA',
        },
        /* Global CSS Reset */
        '*, *::before, *::after': {
          boxSizing: 'border-box',
        },
        // Map hints ("Use two fingers to move the map"); Mapbox's own stylesheet loads later, hence !important
        '.mapboxgl-touch-pan-blocker, .mapboxgl-scroll-zoom-blocker': {
          fontFamily: `${fontFamilyManrope} !important`,
          fontSize: '15px !important',
          fontWeight: 600,
        },
        'body, h1, h2, h3, h4, h5, h6, p, ol, ul': {
          margin: 0,
          padding: 0,
          overflowX: 'hidden',
        },
        // 'hidden' turns these into scroll containers, so a 1px vertical glyph overflow (tight line heights,
        // Manrope) showed a scrollbar next to headings. 'clip' still clips horizontally without that.
        'h1, h2, h3, h4, h5, h6, p, ol, ul': {
          overflowX: 'clip',
        },
        'ol, ul': {
          listStyle: 'none',
        },
        a: {
          textDecoration: 'none',
          color: 'inherit',
        },
        'button, input, select, textarea': {
          appearance: 'none',
          border: 'none',
          background: 'none',
          outline: 'none',
          // Browsers give form controls their own font (Arial / system); use Manrope like the rest of the page
          fontFamily: 'inherit',
        },
        iframe: {
          zIndex: 9999,
        },
        // Cookiebot's floating settings icon stays on screen on every page after the visitor answers;
        // the footer's "Cookie settings" link reopens the banner instead
        '#CookiebotWidget': {
          display: 'none !important',
        },
        '::-webkit-scrollbar': {
          width: '10px',
        },
        '::-webkit-scrollbar-track': {
          background: '#00A98C',
          borderRadius: '20%',
        },
        '::-webkit-scrollbar-thumb': {
          background: '#888',
          borderRadius: '10px',
        },
        '::-webkit-scrollbar-thumb:hover': {
          background: '#555',
        },
        '*': {
          scrollbarWidth: 'thin',
          scrollbarColor: '#00A98C #f1f1f1',
        },
      },
    },
    MuiTablePagination: {
      styleOverrides: {
        root: {
          fontSize: '14px',
        },
        toolbar: {
          height: '56px',
          minHeight: '56px',
          '@media (max-width: 600px)': {
            height: '48px',
            minHeight: '48px',
          },
        },
        select: {
          fontSize: '14px',
        },
        selectIcon: {
          top: 'calc(50% - 12px)',
        },
      },
    },
    MuiButton: {
      styleOverrides: {
        root: {
          minWidth: '100px',
          borderRadius: '999px', // pill buttons
          textTransform: 'none',
          fontWeight: 800,
          border: '1px solid #00A98C',
          cursor: 'pointer',
          backgroundColor: 'transparent',
        },
        text: {
          cursor: 'pointer',
          border: 'none',
          color: '#00A98C',
        },
        containedPrimary: {
          minWidth: '200px',
          color: '#FFFFFF !important',
          border: 'none',
          backgroundColor: '#00A98C',
          boxShadow: '0 6px 16px rgba(0,169,140,0.3)',
          '&:hover': {
            backgroundColor: '#00806A',
            boxShadow: '0 10px 24px rgba(0,169,140,0.35)',
          },
          // Not ready yet (e.g. form incomplete): a faded teal button rather than a grey one that looks broken
          '&.Mui-disabled': {
            backgroundColor: 'rgba(0, 169, 140, 0.35)',
            boxShadow: 'none',
            cursor: 'not-allowed',
          },
        },
        contained: {
          color: '#FFFFFF',
          backgroundColor: '#12211D',
          '&:hover': {
            opacity: '0.9',
            backgroundColor: '#12211D',
          },
          '&:focus': {
            boxShadow: '0 0 0 0.2rem rgb(0 0 0 / 20%)',
          },
        },
        outlined: {
          minWidth: '200px',
          color: '#12211D',
          borderColor: '#12211D',
          backgroundColor: '#ffffff',
          '&:hover': {
            opacity: '0.9',
            backgroundColor: '#D6F1E7',
            color: '#ffffff !important',
          },

          '&:focus': {
            boxShadow: '0 0 0 0.2rem rgb(0 0 0 / 20%)',
            backgroundColor: '#D6F1E7',
            color: '#ffffff !important',
          },
        },
        outlinedPrimary: {
          borderColor: '#00A98C', // Match the border color to the primary color
          color: '#00A98C',
          '&:hover': {
            borderColor: '#00806A', // Match the hover border color to the primary dark color
            backgroundColor: 'white',
          },
        },
      },
    },
    MuiFormHelperText: {
      styleOverrides: {
        root: {
          '&$error': {
            fontSize: '12px',
          },
        },
      },
    },
    MuiInputBase: {
      styleOverrides: {
        input: {
          fontSize: '16px',
          fontFamily: fontFamilyManrope,
          fontWeight: 400,
          color: '#12211D', // black
          borderColor: '#E0E0E0',
          height: 'auto',
          backgroundColor: '#E8F0FE', // Default background color
          '&.Mui-disabled': {
            color: '#A9A9A9', // Replace with your $darkGrey100Color
          },
          '&:not(:placeholder-shown)': {
            backgroundColor: 'transparent', // Transparent background when data is filled
          },
          '&.Mui-focused': {
            borderColor: '#00A98C', // Focus border color
          },
        },
      },
    },
    MuiLinearProgress: {
      styleOverrides: {
        root: {
          height: '5px !important',
          backgroundColor: '#e0e0e0 !important',
          borderRadius: '3px',
        },
        colorPrimary: {
          backgroundColor: '#e0e0e0 !important',
          borderRadius: '3px',
        },
        bar: {
          height: '5px !important',
          borderRadius: '5px',
          backgroundColor: '#12211D', // Replace with your $black color
        },
        barColorPrimary: {
          backgroundColor: '#00A98C', // Primary color for the bar
        },
      },
    },
    // Form fields: rounded corners and the redesign's light outline, teal when focused
    MuiOutlinedInput: {
      styleOverrides: {
        root: {
          padding: '0 !important',
          borderRadius: '12px',
          '&:hover .MuiOutlinedInput-notchedOutline': {
            borderColor: '#B7C6C1',
          },
          '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
            borderColor: '#00A98C',
            borderWidth: '1.5px',
          },
          '&.Mui-error .MuiOutlinedInput-notchedOutline': {
            borderColor: '#E5484D',
          },
          '& input': {
            borderRadius: 'inherit',
          },
        },
        notchedOutline: {
          borderColor: '#D5DEDA',
          // Gap in the outline for the floating label, sized for the 14px label below (the theme doesn't scale it down)
          '& legend': {
            fontSize: '14px',
          },
        },
      },
    },
    MuiInput: {
      styleOverrides: {
        input: {
          '&::placeholder': {
            color: 'pink',
          },
          color: 'black',
        },
      },
    },
    MuiSvgIcon: {
      styleOverrides: {
        root: {
          marginLeft: '5px',
        },
      },
    },
    MuiListItem: {
      styleOverrides: {},
    },
    MuiMenuItem: {
      styleOverrides: {
        root: {
          '&.Mui-selected, &.Mui-selected:hover': {
            backgroundColor: '#D6F1E7 !important',
            color: '#959595',
          },
        },
      },
    },
    MuiStepIcon: {
      styleOverrides: {
        root: {
          color: '#D3D3D3 !important', // Replace with your $lightGrey100Color
          display: 'none !important',
          '&.MuiStepIcon-active': {
            borderRadius: '50%',
          },
          '&.Mui-completed': {
            color: '#32CD32 !important', // Replace with your $green100Color
          },
        },
        text: {
          fontSize: '0.85rem !important',
        },
      },
    },
    MuiStepper: {
      styleOverrides: {
        alternativeLabel: {
          backgroundColor: 'transparent !important',
        },
      },
    },
    MuiCardContent: {
      styleOverrides: {
        root: {
          padding: '4px 16px !important',
        },
      },
    },
    MuiCardHeader: {
      styleOverrides: {
        root: {
          padding: '0 16px 0 0 !important',
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          minHeight: '210px',
        },
      },
    },
    MuiInputLabel: {
      styleOverrides: {
        // Same label size on every field (phone and select fields otherwise inherit 18px)
        root: {
          fontSize: '14px',
        },
        outlined: {
          '&.MuiInputLabel-shrink': {
            transform: 'translate(14px, -6px) scale(1) !important',
          },
        },
      },
    },
    MuiSelect: {
      styleOverrides: {
        select: {
          paddingRight: '0',
          '&:focus': {
            backgroundColor: '#FFFFFF !important', // Replace with your $white color
          },
        },
        iconOutlined: {
          right: '3px !important',
        },
      },
    },
    MuiSnackbarContent: {
      styleOverrides: {
        root: {
          borderRadius: '10px !important',
          '&.expanded': {
            borderRadius: '10px 10px 0 0 !important',
          },
        },
        message: {
          width: '100%',
        },
      },
    },
    MuiDialogContent: {
      styleOverrides: {
        root: {
          padding: '10px',
        },
      },
    },
    MuiDialog: {
      styleOverrides: {
        container: {
          width: '100%',
          margin: '0 auto',
        },
        paper: {
          padding: '1rem 0',
          margin: '0 !important',
          '& .MuiDialogActions-root': {
            padding: '0',
            marginTop: '2rem',
          },
        },
        paperFullWidth: {
          backgroundColor: '#F8F8F8 !important', // Replace with your $alabaster color
        },
      },
    },
    MuiBackdrop: {
      styleOverrides: {
        root: {
          backgroundColor: 'transparent',
        },
      },
    },
    MuiAccordion: {
      styleOverrides: {
        root: {
          minHeight: '68px',
          borderRadius: '0',
          '&::before': {
            backgroundColor: 'transparent',
          },
          boxShadow: 'none',
          borderBottom: '1px solid #E0E0E0',
          marginBottom: '10px',
        },
        rounded: {
          borderRadius: '10px',
        },
      },
    },
    MuiAccordionSummary: {
      styleOverrides: {
        root: {
          padding: '0',
          minHeight: '68px',
        },
        content: {
          margin: '0',
        },
      },
    },
    MuiFormControl: {
      styleOverrides: {
        root: {
          margin: '10px 0 !important',
          '&.popover': {
            padding: '0 10px !important',
          },
        },
      },
    },
    MuiFormControlLabel: {
      styleOverrides: {
        root: {
          width: '100%',
          marginLeft: '0 !important',
          marginRight: '0 !important',
          '&.active': {
            backgroundColor: '#D6F1E7', // Replace with your $green50Color
          },
        },
        label: {
          width: '100%',
        },
      },
    },
    MuiPagination: {
      styleOverrides: {
        ul: {
          justifyContent: 'center',
        },
      },
    },
  },
  palette: {
    common: {
      black: '#12211D', // $black
      white: '#FFFFFF', // $white
    },
    primary: {
      light: '#D6F1E7',
      main: '#00A98C',
      dark: '#00806A', // hover
      contrastText: '#FFFFFF', // $white
    },
    secondary: {
      light: '#FDD9D9', // Assuming a light red color
      main: '#F44242', // $red100Color
      dark: '#F44242', // $red100Color
      contrastText: '#FFFFFF', // $white
    },
    error: {
      light: '#FDECEC',
      main: '#E5484D',
      dark: '#C93A3F',
      contrastText: '#FFFFFF', // $white
    },
    warning: {
      light: '#FFEED6', // Assuming a light yellow color
      main: '#FFBC5B', // $yellow100Color
      dark: '#FFBC5B', // $yellow100Color
      contrastText: '#12211D', // $black
    },
    info: {
      light: '#D1E8F0', // Assuming a light blue color
      main: '#188EB3', // $blue100Color
      dark: '#188EB3', // $blue100Color
      contrastText: '#FFFFFF', // $white
    },
    success: {
      light: '#D6F1E7',
      main: '#00A98C',
      dark: '#00806A',
      contrastText: '#FFFFFF', // $white
    },
    text: {
      primary: '#12211D', // Assuming a dark grey color
      secondary: '#5B6B66', // $darkGrey100Color
      disabled: '#BDBDBD', // $grey100Color
    },
    grey: {
      A100: '#F9F9F9', // $alabaster
      A200: '#E0E0E0', // $lightGrey100Color
      A400: '#BDBDBD', // $grey100Color
      A700: '#5B6B66', // $darkGrey100Color
    },
    divider: '#E3EAE7',
    background: {
      default: '#F7F9F8', // page background in the new design
      paper: '#FFFFFF',
    },
  },
  shape: {
    borderRadius: 4,
  },
  typography: {
    fontFamily: fontFamilyManrope, // base for MUI defaults and theme.typography.fontFamily (nav, footer)
    htmlFontSize: 16,
    fontSize: 16,
    // Using '1.5' (unitless) is generally recommended for line-height
    // It means 1.5 * the element's font-size
    h1: {
      fontFamily: fontFamilyManrope,
      fontWeight: 800,
      color: colorPrimary,
      fontSize: '2.25rem', // 36px
      letterSpacing: '-1px',
      lineHeight: '1.4', // Example: Tighter line height for large heading
      '@media (max-width: 767px)': {
        fontSize: '1.5rem', // 24px
      },
    },
    h2: {
      fontFamily: fontFamilyManrope,
      fontWeight: 800,
      fontSize: '1.75rem', // 28px
      letterSpacing: '-0.5px',
      lineHeight: '1.4', // Example: Slightly tighter line height for h2
      color: colorPrimary,
      '@media (max-width: 767px)': {
        fontSize: '1.25rem', // 20px
      },
    },
    h3: {
      fontFamily: fontFamilyManrope,
      fontWeight: 800,
      fontSize: '1.5rem', // 24px
      color: '#12211D',
      letterSpacing: '-0.3px',
      lineHeight: '1.4', // Example: Slightly tighter line height for h3
      '@media (max-width: 767px)': {
        fontSize: '1.125rem', // 18px
      },
    },
    h4: {
      fontFamily: fontFamilyManrope,
      fontWeight: 800,
      letterSpacing: '-0.2px',
      fontSize: '1rem', // 16px
      lineHeight: '1.5', // Standard line height might be fine here
      color: '#12211D',
    },
    body1: {
      fontFamily: fontFamilyManrope,
      fontWeight: fontWeightSemibold,
      fontSize: '1.125rem', // 18px
      letterSpacing: '-0.27px',
      lineHeight: '1.5', // Standard line height for body text
      color: '#12211D',
    },
    body2: {
      fontFamily: fontFamilyManrope,
      fontWeight: fontWeightMedium,
      fontSize: '1.125rem', // 18px
      letterSpacing: '-0.27px',
      lineHeight: '1.5', // Standard line height for body text
    },
    body3: {
      fontFamily: fontFamilyManrope,
      fontWeight: fontWeightSemibold,
      fontSize: '0.875rem', // 14px
      lineHeight: '1.5',
      letterSpacing: '-0.25px',
    },
    body4: {
      fontFamily: fontFamilyManrope,
      fontWeight: fontWeightMedium,
      fontSize: '0.875rem', // 14px
      lineHeight: '1.5',
      letterSpacing: '-0.25px',
      color: '#5B6B66',
    },
    body5: {
      fontFamily: fontFamilyManrope,
      fontWeight: fontWeightMedium,
      fontSize: '0.75rem', // 12px
      lineHeight: '1.5',
      letterSpacing: '-0.25px',
    },
    mainButton: {
      fontFamily: fontFamilyManrope,
      fontWeight: fontWeightBold,
      fontSize: '1rem', // 16px
      lineHeight: '1.5', // Often buttons use line-height for vertical centering too
      letterSpacing: '-0.25px',
      textTransform: 'none',
    },
    ctaButton: {
      fontFamily: fontFamilyManrope,
      fontWeight: fontWeightBold,
      fontSize: '2rem', // 32px
      lineHeight: '1.4', // Example: Potentially adjust for large button text
      letterSpacing: '-0.25px',
      textTransform: 'none',
      // Assuming createTheme().breakpoints.down('sm') is correctly evaluated
      // FIX: Added 'rem' unit to the fontSize
      [createTheme().breakpoints.down('sm')]: {
        fontSize: '1.625rem', // Was '1.625', now 26px
      },
    },
  },
});
